import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Radio, Timer, Music, Users, CheckCircle, Clock, Volume2, Mic, Heart, ListMusic, Plus, Share2, QrCode, Copy, ChevronLeft, ChevronRight, Calendar, TrendingUp, Search, X, DollarSign, Download, MessageCircle } from 'lucide-react';
import { getMobileStyles, ysr2Mobile } from '../styles/mobileDesignSystem';
import MobileCard from './MobileCard';
import MobileOnboarding from './MobileOnboarding';
import AddSongWizard from './AddSongWizard';
import AddSongsRequiredModal from '@/components/AddSongsRequiredModal';
import MobileQRCode from './MobileQRCode';
import { getSessionHistory } from '@/lib/sessionHistory';
import { doc, collection, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { isAddSongWizardCompleted, persistAddSongWizardCompleted, ensureWizardDoneIfHasSongs } from '@/lib/addSongWizardState';
import UserNotificationCenter from './UserNotificationCenter';
import { useToast } from '@/components/ui/use-toast';
import QRCode from 'qrcode.react';
import { buildArtistPublicUrl } from '@/lib/urlHelpers';
import { capitalizeWords } from '@/lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { formatCurrency, getCurrencySymbol } from '@/lib/currency';
// Utility function to format time ago
const formatTimeAgo = (timestamp, currentTime) => {
  if (!timestamp) return 'Unknown';
  
  const requestTime = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const diffMs = currentTime - requestTime;
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins === 1) return '1m ago';
  if (diffMins < 60) return `${diffMins}m ago`;
  
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours === 1) return '1h ago';
  if (diffHours < 24) return `${diffHours}h ago`;
  
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return '1d ago';
  return `${diffDays}d ago`;
};

const MobileLiveDashboard = ({ 
  isLive, 
  sessionData, 
  requests, 
  tips, 
  setlists,
  songs = [], // Add songs prop for count
  profile,
  activePlaylistId,
  onMarkAsPlayed,
  onMarkAsSkipped,
  onSelectSetlist,
  onNavigateToCatalogue,
  onGoLive,
  onNavigateToSongs, // Add navigation to songs
  onShowStartModal, // Add show start modal prop
  user, // Add user prop for session history
  showSessionComplete, // Add session summary props
  sessionCompleteData,
  onCloseSessionComplete,
  // Trial mode props
  isTrialMode = false,
  trialInstructionText = '',
  isTypingInstruction = false,
  showTrialDemoModal = false,
  setShowTrialDemoModal,
  showTrialPrompt = false,
  setShowTrialPrompt,
  showTrialPillButton = false,
  trialPillTextIndex = 0,
  hasGoneLiveBefore = false,
  daysSinceLastLive = null,
  onStartTrialDemo,
  highlightMarkAsPlayedSongId = null,
  highlightRequestCount = null,
  goLivePillPulse = false,
  showTrialTipPopup = false,
  trialTipData = null,
  setShowTrialTipPopup,
  // Pause state must be honoured here too, or mobile shows a different queue
  // to desktop after the artist taps "Pause New Requests".
  acceptingNewRequests = true,
  requestsPausedAt = null,
  onToggleAcceptingRequests
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('requests');
  const [showShareSection, setShowShareSection] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [showSongWizard, setShowSongWizard] = useState(false);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showSongSearchModal, setShowSongSearchModal] = useState(false);
  const [songSearchQuery, setSongSearchQuery] = useState('');
  const [filteredSongs, setFilteredSongs] = useState([]);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [hasSongs, setHasSongs] = useState(false);
  const [showAddSongsModal, setShowAddSongsModal] = useState(false);
  const [trialPermanentlyDismissed, setTrialPermanentlyDismissed] = useState(false);
  const { toast } = useToast();
  const publicUrl = useMemo(
    () => (profile?.url_slug ? buildArtistPublicUrl(profile.url_slug) : null),
    [profile?.url_slug]
  );

  const artistCcy = useMemo(
    () => (profile?.currency || 'GBP').toString().toUpperCase(),
    [profile?.currency],
  );
  const fmtMoney = (n) => formatCurrency(Number(n) || 0, artistCcy);

  const dismissTrialDemoPermanently = () => {
    if (setShowTrialPrompt) {
      setShowTrialPrompt(false);
    }
    if (setShowTrialDemoModal) {
      setShowTrialDemoModal(false);
    }
    setTrialPermanentlyDismissed(true);

    if (user?.uid) {
      localStorage.setItem(`trial_demo_dismissed_permanent_${user.uid}`, 'true');
    }
    if (user?.email !== 'trial@trial.com') {
      localStorage.setItem('trial_demo_dismissed', 'true');
    }
  };

  // Update time every minute for "time ago" display
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  // Read persistent dismissal flag for the trial demo pill / prompt
  useEffect(() => {
    if (!user?.uid) return;
    try {
      const dismissed = localStorage.getItem(`trial_demo_dismissed_permanent_${user.uid}`) === 'true';
      setTrialPermanentlyDismissed(dismissed);
    } catch (err) {
      console.warn('Unable to read trial demo dismissal flag:', err);
    }
  }, [user?.uid]);

  // Handle keyboard visibility for modal
  useEffect(() => {
    if (!showSongSearchModal) return;

    const handleViewportChange = () => {
      if (window.visualViewport) {
        const viewportHeight = window.visualViewport.height;
        const windowHeight = window.innerHeight;
        const keyboardHeight = Math.max(0, windowHeight - viewportHeight);
        setKeyboardHeight(keyboardHeight);
      } else {
        // Fallback for browsers without Visual Viewport API
        // Estimate keyboard height based on window resize
        const initialHeight = window.innerHeight;
        const currentHeight = window.innerHeight;
        const estimatedKeyboardHeight = Math.max(0, initialHeight - currentHeight);
        setKeyboardHeight(estimatedKeyboardHeight);
      }
    };

    // Listen for viewport changes (keyboard show/hide)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
      handleViewportChange(); // Initial call
    } else {
      // Fallback: listen to window resize
      window.addEventListener('resize', handleViewportChange);
      handleViewportChange(); // Initial call
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
      } else {
        window.removeEventListener('resize', handleViewportChange);
      }
    };
  }, [showSongSearchModal]);

  // Autoplay onboarding only when explicitly triggered (signup flow or Quick Tour)
  useEffect(() => {
    const triggerOnboarding = localStorage.getItem('ysr_trigger_onboarding') === 'true';
    if (triggerOnboarding) {
      setShowOnboarding(true);
      localStorage.removeItem('ysr_trigger_onboarding');
    }
  }, []);

  useEffect(() => {
    if (!user?.uid) return;
    void ensureWizardDoneIfHasSongs(db, user.uid, songs?.length ?? 0);
  }, [user?.uid, songs?.length]);

  // Song wizard: only from explicit trigger; never again after completion or if catalogue exists
  useEffect(() => {
    const showSongWizardTrigger = localStorage.getItem('ysr_show_song_wizard') === 'true';
    const wizardDone = isAddSongWizardCompleted(profile, songs?.length ?? 0);

    if (wizardDone) {
      if (showSongWizardTrigger) localStorage.removeItem('ysr_show_song_wizard');
      if (showSongWizard) setShowSongWizard(false);
      return;
    }

    if (user && !showSongWizard && showSongWizardTrigger) {
      localStorage.removeItem('ysr_show_song_wizard');
      setShowSongWizard(true);
    }
  }, [user, showSongWizard, songs?.length, profile?.add_song_wizard_completed]);

  // Check onboarding and songs status
  useEffect(() => {
    const checkStatus = () => {
      const onboardingCompleted = localStorage.getItem('ysr_onboarding_completed') === 'true';
      const songsAdded = localStorage.getItem('ysr_songs_added_first_time') === 'true';
      
      setHasCompletedOnboarding(onboardingCompleted);
      setHasSongs(songsAdded);
    };
    
    // Check immediately
    checkStatus();
    
    // Listen for storage changes
    const handleStorageChange = (e) => {
      if (e.key === 'ysr_onboarding_completed' || e.key === 'ysr_songs_added_first_time') {
        checkStatus();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically in case of same-tab changes
    const interval = setInterval(checkStatus, 1000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Load session history
  useEffect(() => {
    const loadSessionHistory = async () => {
      if (!user?.uid) {
        console.log('🔍 DEBUG: No user UID available for session history');
        return;
      }
      
      console.log('🔍 DEBUG: Loading session history for user:', user.uid);
      setLoadingHistory(true);
      try {
        const history = await getSessionHistory(user.uid, 5);
        console.log('🔍 DEBUG: Session history loaded:', history);
        setSessionHistory(history);
      } catch (error) {
        console.error('❌ Error loading session history:', error);
      } finally {
        setLoadingHistory(false);
      }
    };

    loadSessionHistory();
  }, [user?.uid]);

  // Refresh session history when going offline (in case a new session just ended)
  useEffect(() => {
    if (!isLive && user?.uid) {
      console.log('🔍 DEBUG: User went offline, refreshing session history');
      const refreshSessionHistory = async () => {
        try {
          const history = await getSessionHistory(user.uid, 5);
          console.log('🔍 DEBUG: Session history refreshed after going offline:', history);
          setSessionHistory(history);
        } catch (error) {
          console.error('❌ Error refreshing session history:', error);
        }
      };
      
      // Add a small delay to ensure the session history has been stored
      setTimeout(refreshSessionHistory, 1000);
    }
  }, [isLive, user?.uid]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    setHasCompletedOnboarding(true);
    localStorage.setItem('ysr_onboarding_completed', 'true');
    localStorage.setItem('ysr_just_completed_onboarding', 'true');
    
    // Ensure live status is OFF after onboarding completion
    // The onboarding is just a visual simulation and should not affect real live status
    // If the user somehow ends up live after onboarding, this is a bug that needs investigation
    if (isLive) {
      console.warn('Onboarding completed but user is still live - this should not happen');
      // Note: We don't force go offline here as the onboarding should not affect real state
      // This is just for debugging purposes
    }
    
    // Don't automatically show the song wizard - let user choose to click Catalogue or Go Live
    // The navbar will be grey with only Catalogue glowing to guide them
  };

  const handleSongWizardComplete = async () => {
    setShowSongWizard(false);
    await persistAddSongWizardCompleted(db, user.uid);
    setTimeout(() => {
      window.location.href = '/admin';
    }, 500);
  };

  // Any non-'played' request is incoming, regardless of the status string a
  // payment flow happened to set. Mirrors the desktop filter.
  const pendingRequests = useMemo(() => {
    let pending = (requests || []).filter((r) => (r.status || '').toLowerCase() !== 'played');

    if (!acceptingNewRequests && requestsPausedAt && !isTrialMode) {
      pending = pending.filter((req) => {
        if (req.is_admin_demo === true) return true;
        const createdAt = req.created_at?.toDate ? req.created_at.toDate() : new Date(req.created_at);
        return createdAt <= requestsPausedAt;
      });
    }

    return pending;
  }, [requests, acceptingNewRequests, requestsPausedAt, isTrialMode]);

  const playedRequests = useMemo(
    () => (requests || []).filter(r => r.status === 'played'),
    [requests]
  );
  
  // Group pending requests by song and calculate totals
  const groupedPendingRequests = useMemo(() => {
    const grouped = pendingRequests.reduce((acc, request) => {
      const songId = request.song_id ? request.song_id : request.id;
      if (!acc[songId]) {
        acc[songId] = {
          song_id: songId,
          song_title: request.song_title,
          original_artist: request.original_artist,
          total_amount: 0,
          total_song_price: 0,
          total_tips: 0,
          request_count: 0,
          all_request_ids: [],
          requests: [],
          requester_names: []
        };
      }
      acc[songId].total_amount += (request.amount_paid || 0);
      // Calculate song price (amount_paid - tip)
      const songPrice = (request.amount_paid || 0) - (request.tip || 0);
      acc[songId].total_song_price += songPrice;
      acc[songId].total_tips += (request.tip || 0);
      acc[songId].request_count += 1;
      acc[songId].all_request_ids.push(request.id);
      acc[songId].requests.push(request);
      if (request.requester_name && !acc[songId].requester_names.includes(request.requester_name)) {
        acc[songId].requester_names.push(request.requester_name);
      }
      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => b.total_amount - a.total_amount || a.song_title.localeCompare(b.song_title));
  }, [pendingRequests]);
  

  const tabs = [
    { id: 'requests', label: 'Requests', icon: Music, count: groupedPendingRequests.length },
    { id: 'played', label: 'Played', icon: CheckCircle, count: playedRequests.length },
    { id: 'setlists', label: 'Setlists', icon: ListMusic, count: setlists?.length || 0 },
    { id: 'stats', label: 'Stats', icon: Users, count: null },
  ];

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatSessionDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  // Song search functionality
  const handleSongSearch = (query) => {
    setSongSearchQuery(query);
    if (!query.trim()) {
      setFilteredSongs([]);
      return;
    }

    const filtered = songs.filter(song => 
      song.title.toLowerCase().includes(query.toLowerCase()) ||
      (song.artist && song.artist.toLowerCase().includes(query.toLowerCase()))
    );
    setFilteredSongs(filtered.slice(0, 10)); // Limit to 10 results
  };

  // Mark song as played manually
  const markSongAsPlayedFromSearch = async (song) => {
    if (!user || !sessionData?.sessionId) return;

    try {
      console.log("🎵 Marking song as played manually:", song.title);
      
      // Create a new request document for this song
      const requestRef = doc(collection(db, 'requests'));
      await setDoc(requestRef, {
        artist_id: user.uid,
        song_id: song.id,
        song_title: song.title,
        original_artist: song.artist || 'Unknown Artist',
        requester_name: 'Artist (Manual)',
        status: 'played',
        session_id: sessionData.sessionId,
        amount_paid: 0,
        tip: 0,
        created_at: serverTimestamp(),
        played_at: serverTimestamp()
      });

      console.log("✅ Successfully marked song as played manually:", song.title);
      setShowSongSearchModal(false);
      setSongSearchQuery('');
      setFilteredSongs([]);
      
      // You could add a toast notification here
    } catch (error) {
      console.error("❌ Error marking song as played manually:", error);
      // You could add an error toast here
    }
  };


  const trialInstructionOverlay = isTrialMode && trialInstructionText
    ? createPortal(
        <AnimatePresence>
          <motion.div
            className="fixed left-0 right-0 z-[2147483000] px-4 pointer-events-none"
            style={{ top: 'calc(env(safe-area-inset-top, 0px) + 148px)' }}
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
          >
            <motion.div
              className="mx-auto w-full max-w-sm rounded-[1.75rem] border border-blue-300/35 bg-slate-950/90 p-[1px] shadow-[0_24px_80px_rgba(37,99,235,0.38)] backdrop-blur-2xl"
              initial={{ scale: 0.96 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.96 }}
              transition={{ type: 'spring', damping: 24, stiffness: 260 }}
            >
              <div className="rounded-[1.7rem] bg-gradient-to-br from-blue-500/15 via-slate-950/95 to-purple-500/15 px-5 py-4">
                <div className="mb-2 flex items-center justify-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-emerald-300 shadow-[0_0_16px_rgba(110,231,183,0.8)]" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-blue-100/75">Demo Guide</span>
                </div>
                <div className="text-center text-base font-semibold leading-snug text-white whitespace-pre-line">
                  {trialInstructionText}
                  {isTypingInstruction && <span className="animate-pulse text-blue-300">|</span>}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )
    : null;

  const trialTipOverlay = showTrialTipPopup && trialTipData
    ? createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: -16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: -16 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="fixed left-0 right-0 z-[2147483001] flex justify-center px-4 pointer-events-none"
            style={{ top: 'calc(env(safe-area-inset-top, 0px) + 76px)' }}
          >
            <motion.div className="w-full max-w-sm pointer-events-auto">
              <Card className="rounded-[1.75rem] bg-gradient-to-br from-rose-500/25 via-slate-950/95 to-pink-500/20 border border-rose-300/35 shadow-[0_24px_80px_rgba(244,63,94,0.38)] backdrop-blur-2xl">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-red-500/20 rounded-2xl flex items-center justify-center border border-red-300/50">
                        <Heart className="w-6 h-6 text-red-300 fill-red-300 animate-pulse" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-white font-bold text-sm leading-tight">You Received a Tip!</h3>
                        <button
                          onClick={() => setShowTrialTipPopup && setShowTrialTipPopup(false)}
                          className="text-red-100/80 hover:text-white transition-colors flex-shrink-0 ml-2"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-baseline space-x-1 flex-wrap">
                          <span className="text-red-100/80 text-xs">from</span>
                          <span className="text-white font-semibold text-sm break-words">{trialTipData.requester_name}</span>
                        </div>
                        <div className="text-xl font-black text-white">{fmtMoney(trialTipData.amount)}</div>
                        {trialTipData.message && (
                          <p className="text-red-50/90 text-xs italic mt-1 break-words">"{trialTipData.message}"</p>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )
    : null;



  return (
    <div className={`${ysr2Mobile.shell} h-[100dvh] w-full max-w-none flex flex-col`}>
      {trialInstructionOverlay}
      {trialTipOverlay}
      <div className={ysr2Mobile.orbBlue} />
      <div className={ysr2Mobile.orbPurple} />
      {/* Onboarding Overlay */}
      <MobileOnboarding 
        isVisible={showOnboarding} 
        onComplete={handleOnboardingComplete}
      />
      
      {/* Song Wizard Overlay */}
      <AddSongWizard
        isVisible={showSongWizard}
        onComplete={handleSongWizardComplete}
        onDismiss={() => setShowSongWizard(false)}
      />
      {/* Fixed Header - Compact */}
      <div className="relative z-10 flex-shrink-0 px-3 pt-3 pb-2">
        <motion.div
          className={`${ysr2Mobile.surfaceStrong} p-3`}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <motion.button
                onClick={() => {
                  if (!isLive) {
                    // CRITICAL: Check if user has songs in the database - NO EXCEPTIONS!
                    console.log('🔍 DEBUG: Purple toggle - Checking songs array:', songs);
                    console.log('🔍 DEBUG: Purple toggle - Songs array length:', songs?.length);
                    
                    if (!songs || songs.length === 0) {
                      console.log('🚫 CRITICAL: No songs found in database, blocking goLive from purple toggle');
                      setShowAddSongsModal(true);
                      return;
                    }
                    
                    // Show session duration modal first
                    onShowStartModal && onShowStartModal();
                  } else {
                    // If live, this could trigger go offline - but let's not do that automatically
                    // The user should use the proper offline flow
                    console.log('🔍 DEBUG: Purple toggle clicked while live - no action taken');
                  }
                }}
                className={`w-11 h-11 rounded-2xl flex items-center justify-center transition-all duration-200 ${
                  isLive ? `bg-emerald-500/15 border border-emerald-300/35 shadow-[0_0_28px_rgba(16,185,129,0.22)] ${isTrialMode ? 'drop-shadow-[0_0_12px_rgba(34,197,94,0.9)] shadow-[0_0_20px_rgba(34,197,94,0.6)]' : ''}` : 'bg-blue-500/10 border border-blue-300/30 hover:bg-blue-500/20'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                disabled={isLive}
              >
                <Radio className={`w-5 h-5 ${isLive ? `text-emerald-300 ${isTrialMode ? 'drop-shadow-[0_0_8px_rgba(34,197,94,0.8)]' : ''}` : 'text-blue-200'}`} />
              </motion.button>
              <div className="text-left">
              <h1 className={`text-2xl font-black tracking-tight text-white ${isTrialMode && isLive ? 'drop-shadow-[0_0_8px_rgba(34,197,94,0.8)]' : ''}`}>
                {isLive ? 'LIVE' : 'Offline'}
              </h1>
              <p className="text-[11px] uppercase tracking-[0.22em] text-blue-100/70">Artist control room</p>
              </div>
              {isLive && sessionData?.timeLeft && (
                <div className="flex items-center space-x-1 ml-2 rounded-full border border-emerald-300/20 bg-emerald-500/10 px-2 py-1">
                  <Timer className="w-3 h-3 text-emerald-300" />
                  <span className="text-sm font-mono text-emerald-200">
                    {sessionData.timeLeft}
                  </span>
                </div>
              )}
            </div>
            <UserNotificationCenter />
          </div>
        </motion.div>
      </div>

      {/* Share Your Public Page Section - Redesigned for Mobile */}
      {profile?.url_slug && (
        <motion.div
          initial={{ opacity: 1, height: 'auto' }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
          style={{ overflow: showShareSection ? 'visible' : 'hidden' }}
          className={`relative flex-shrink-0 px-3 pb-3 ${showShareSection ? 'z-[100000]' : 'z-10'}`}
        >
          <motion.button 
            className={`w-full ${ysr2Mobile.glowCard} p-4 active:scale-[0.98] transition-all`}
            onClick={() => setShowShareSection(!showShareSection)}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-500/15 border border-blue-300/20 rounded-2xl p-2">
                  <Share2 className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <span className="text-base font-bold text-white block">Share Your Profile</span>
                  <span className="text-xs text-blue-100/75">QR code and public link</span>
                </div>
              </div>
              <div className="bg-white/10 border border-white/10 rounded-full p-1.5">
                {showShareSection ? (
                  <ChevronLeft className="w-4 h-4 text-white rotate-90" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-white rotate-90" />
                )}
              </div>
            </div>
          </motion.button>
          
          {/* Share Modal - Fully Scrollable Bottom Sheet */}
          {createPortal((
            <AnimatePresence>
              {showShareSection && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/75 backdrop-blur-sm z-[100000] flex items-end"
                onClick={() => setShowShareSection(false)}
              >
                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                  className="w-full bg-[#050816] border-t border-blue-300/20 rounded-t-[2rem] shadow-2xl flex flex-col"
                  style={{ maxHeight: '90vh' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header with Close Button - Fixed */}
                  <div className="flex-shrink-0 px-6 py-4 border-b border-blue-300/10 bg-slate-950/80 backdrop-blur-xl">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-xl font-bold text-white">Share Your Profile</h3>
                        <p className="text-sm text-blue-100/65 mt-1">Share your QR code or link</p>
                      </div>
                      <button
                        onClick={() => setShowShareSection(false)}
                        className="p-3 bg-white/10 hover:bg-white/15 text-white rounded-full transition-colors shadow-lg border border-white/10"
                        aria-label="Close"
                      >
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Fully Scrollable Content */}
                  <div 
                    className="flex-1 overflow-y-auto overscroll-contain"
                    style={{ 
                      WebkitOverflowScrolling: 'touch',
                      scrollBehavior: 'smooth'
                    }}
                  >
                    <div className="px-6 py-6 space-y-6 pb-8">
                      {/* QR Code - Smaller */}
                      <div className="flex flex-col items-center">
                        <div className="bg-white p-4 rounded-2xl shadow-xl mb-3" data-qr-code>
                          <QRCode 
                            value={publicUrl || ''}
                            size={140} 
                            level="H" 
                            includeMargin={true}
                          />
                        </div>
                        <p className="text-sm text-gray-400 text-center">
                          Scan to access your artist page
                        </p>
                      </div>

                      {/* URL Section */}
                      <div className={`${ysr2Mobile.surface} p-4`}>
                        <label className="text-xs font-medium text-gray-400 mb-2 block">Your Profile URL</label>
                        <div className="flex items-center gap-2">
                          <input
                            readOnly
                            value={publicUrl || ''}
                            className={`${ysr2Mobile.input} flex-1 text-sm`}
                          />
                          <button
                            onClick={() => {
                              if (publicUrl) {
                                navigator.clipboard.writeText(publicUrl);
                              }
                              toast({
                                title: "Copied!",
                                description: "URL copied to clipboard",
                              });
                            }}
                            className={`${ysr2Mobile.primaryButton} p-3 flex-shrink-0`}
                          >
                            <Copy className="w-5 h-5 text-white" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Share Actions */}
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={async () => {
                            if (navigator.share) {
                              try {
                                await navigator.share({
                                  title: 'Check out my artist page!',
                                  text: `Check out ${profile.artist_name || 'my'} on Your Song Request`,
                                  url: publicUrl || undefined,
                                });
                                setShowShareSection(false);
                              } catch (err) {
                                if (err.name !== 'AbortError') {
                                  console.error('Error sharing:', err);
                                }
                              }
                            } else {
                              if (publicUrl) {
                                navigator.clipboard.writeText(publicUrl);
                              }
                              toast({
                                title: "Copied!",
                                description: "URL copied to clipboard",
                              });
                            }
                          }}
                          className="flex flex-col items-center justify-center p-4 bg-blue-500/10 border border-blue-300/25 rounded-2xl hover:bg-blue-500/15 active:bg-blue-500/20 transition-colors"
                        >
                          <Share2 className="w-6 h-6 text-blue-400 mb-2" />
                          <span className="text-sm font-medium text-blue-300">Share Link</span>
                        </button>
                        
                        <button
                          onClick={() => {
                            const qrElement = document.querySelector('[data-qr-code]');
                            if (qrElement) {
                              const canvas = qrElement.querySelector('canvas');
                              if (canvas) {
                                const link = document.createElement('a');
                                link.download = `qr-code-${profile.url_slug}.png`;
                                link.href = canvas.toDataURL('image/png');
                                link.click();
                                toast({
                                  title: "Downloaded!",
                                  description: "QR code downloaded",
                                });
                              }
                            }
                          }}
                          className="flex flex-col items-center justify-center p-4 bg-emerald-500/10 border border-emerald-300/25 rounded-2xl hover:bg-emerald-500/15 active:bg-emerald-500/20 transition-colors"
                        >
                          <Download className="w-6 h-6 text-green-400 mb-2" />
                          <span className="text-sm font-medium text-green-300">Download QR</span>
                        </button>
                      </div>
                      
                      {/* Close Button at Bottom */}
                      <button
                        onClick={() => setShowShareSection(false)}
                        className={`w-full py-3 px-4 ${ysr2Mobile.secondaryButton} font-medium`}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
              )}
            </AnimatePresence>
          ), document.body)}
        </motion.div>
      )}



      {/* Fixed 2x2 Grid Navigation for Live Mode */}
      {isLive && (
        <div className="relative z-10 flex-shrink-0 px-3 pb-3">
          <div className="grid grid-cols-2 gap-2">
            {tabs.map((tab, index) => (
              <motion.button
                key={tab.id}
                className={`flex items-center justify-center py-2.5 px-2 rounded-2xl border transition-all duration-200 backdrop-blur-xl ${
                  activeTab === tab.id
                    ? 'bg-blue-500/15 border-blue-300/35 text-blue-100 shadow-[0_0_24px_rgba(59,130,246,0.16)]'
                    : 'bg-white/[0.045] border-white/10 text-slate-400'
                }`}
                onClick={() => setActiveTab(tab.id)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="text-xs font-medium text-center">{tab.label}</span>
                {tab.count !== null && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ml-1.5 ${
                    activeTab === tab.id 
                      ? 'bg-blue-500/25 text-blue-100' 
                      : 'bg-white/10 text-slate-400'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </motion.button>
            ))}
          </div>
        </div>
      )}

      {/* Scrollable Content Area */}
      <div className="relative z-10 flex-1 overflow-y-auto pb-28">
        {/* Quick Actions - Only show when offline */}
        {!isLive && (
          <div className="p-4">
            {/* Quick Actions */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Users className="w-5 h-5 text-blue-300" />
                <h3 className="text-lg font-black text-white">Quick Actions</h3>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {/* Songs */}
                <motion.button
                  onClick={() => onNavigateToSongs && onNavigateToSongs()}
                  className={`${ysr2Mobile.surface} text-center p-4 transition-all duration-200 active:scale-[0.98]`}
                  whileTap={{ scale: 0.95 }}
                >
                  <Music className="w-5 h-5 text-blue-300 mx-auto mb-2" />
                  <div className="text-2xl font-black text-white">{songs?.length || 0}</div>
                  <div className="text-xs text-slate-400">Songs</div>
                </motion.button>

                {/* Setlists */}
                <motion.button
                  onClick={() => onNavigateToCatalogue && onNavigateToCatalogue()}
                  className={`${ysr2Mobile.glowCard} text-center p-4 transition-all duration-200 active:scale-[0.98]`}
                  whileTap={{ scale: 0.95 }}
                >
                  <ListMusic className="w-5 h-5 text-purple-200 mx-auto mb-2" />
                  <div className="text-2xl font-black text-white">{setlists?.length || 0}</div>
                  <div className="text-xs text-slate-400">Setlists</div>
                </motion.button>
              </div>
            </div>

            {/* Trial Demo Pulsing Pill Button - Underneath Quick Actions */}
            <AnimatePresence>
              {showTrialPillButton && !isLive && !isTrialMode && !trialPermanentlyDismissed && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5 }}
                  className="flex justify-center mt-8 mb-6 px-4"
                >
                  <motion.button
                    onClick={() => {
                      // When tapping the pill we explicitly (re)enable the demo,
                      // but only if it hasn't been permanently dismissed.
                      if (trialPermanentlyDismissed) return;
                      try {
                        localStorage.removeItem('trial_demo_dismissed');
                      } catch (err) {
                        console.warn('Unable to clear trial demo temporary dismissal:', err);
                      }
                      onStartTrialDemo && onStartTrialDemo();
                    }}
                    className="border-2 border-white/80 text-white rounded-full px-8 py-3 font-semibold backdrop-blur-sm active:scale-[0.98] transition-all"
                    style={{ 
                      opacity: 0.8,
                      background: 'rgba(255, 255, 255, 0.05)',
                      boxShadow: '0 0 20px rgba(255, 255, 255, 0.3), 0 0 40px rgba(255, 255, 255, 0.1)'
                    }}
                    whileTap={{ scale: 0.98 }}
                    animate={{
                      scale: [1, 1.02, 1],
                      boxShadow: [
                        '0 0 20px rgba(255, 255, 255, 0.3), 0 0 40px rgba(255, 255, 255, 0.1)',
                        '0 0 30px rgba(255, 255, 255, 0.5), 0 0 60px rgba(255, 255, 255, 0.2)',
                        '0 0 20px rgba(255, 255, 255, 0.3), 0 0 40px rgba(255, 255, 255, 0.1)'
                      ]
                    }}
                    transition={{
                      scale: {
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      },
                      boxShadow: {
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }
                    }}
                  >
                    <motion.span
                      key={trialPillTextIndex}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {trialPillTextIndex === 0 ? "First Time Going Live?" : "Tap Here For a Demo!"}
                    </motion.span>
                  </motion.button>
                </motion.div>
              )}
            </AnimatePresence>

          </div>
        )}

        {/* Live Content - Static Cards */}
        {isLive && (
          <div className="w-full">
            {/* Trial Demo Prompt - Show before content cards */}
            {showTrialPrompt && setShowTrialPrompt && !isTrialMode && !trialPermanentlyDismissed && songs.length >= 3 && (
              <div className="px-3 py-3">
                <Card className="bg-gradient-to-r from-blue-900/40 to-purple-900/40 border-blue-700/50">
                  <CardContent className="p-4">
                    <div className="flex flex-col space-y-3">
                      <div className="flex items-center space-x-4">
                        <div className="text-4xl">🎵</div>
                        <div className="flex-1">
                          <h3 className="text-white font-semibold text-lg mb-1">
                            {hasGoneLiveBefore && daysSinceLastLive !== null && daysSinceLastLive > 7
                              ? "Need a refresher? Take a quick trial!"
                              : "First Time Going Live? Try the Demo!"}
                          </h3>
                          <p className="text-gray-300 text-sm">
                            See how song requests work with an interactive demo using your own songs.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          onClick={() => {
                            localStorage.removeItem('trial_demo_dismissed');
                            onStartTrialDemo && onStartTrialDemo();
                          }}
                          className="bg-blue-600 hover:bg-blue-700 text-white flex-1"
                          size="sm"
                        >
                          Start Demo
                        </Button>
                        <Button
                          onClick={() => {
                            dismissTrialDemoPermanently();
                            toast({
                              title: "Demo Hidden",
                              description: "The demo prompt and pill are hidden for this artist.",
                            });
                          }}
                          variant="ghost"
                          className="text-gray-400 hover:text-white"
                          size="sm"
                        >
                          Do not show again
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* Content Cards */}
            <AnimatePresence mode="wait">
            {activeTab === 'requests' && (
              <motion.div
                key="requests"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
                className="w-full"
              >
                <div className="px-3 py-4">
                  <div className={`${ysr2Mobile.surface} flex items-center justify-between mb-4 p-4`}>
                    <div className="flex items-center space-x-2">
                      <Music className="w-5 h-5 text-blue-300" />
                      <h3 className="text-lg font-black text-white">Song Requests</h3>
                      {isTrialMode && (
                        <span className="px-2 py-1 bg-yellow-600/20 text-yellow-400 text-xs rounded-full">Trial Demo</span>
                      )}
                    </div>
                    <span className="text-sm text-blue-200">{groupedPendingRequests.length} pending</span>
                  </div>
                  
                  {groupedPendingRequests.length === 0 ? (
                    <div className={`${ysr2Mobile.surface} text-center py-10 px-5`}>
                      <Music className="w-12 h-12 text-blue-200 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-white mb-2">No Pending Requests</h3>
                      <p className="text-blue-100/75 text-sm">
                        Song requests from your audience will appear here
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {groupedPendingRequests.map((groupedRequest, index) => (
                        <motion.div
                          key={groupedRequest.song_id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                          className={`${ysr2Mobile.surface} relative p-4 transition-colors`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              {/* Song Title Row */}
                                <div className="flex items-center space-x-2 mb-1">
                                <h4 className="text-white font-semibold text-lg leading-tight break-words flex-1">{capitalizeWords(groupedRequest.song_title)}</h4>
                                <div className="flex items-center space-x-1 flex-shrink-0">
                                  {groupedRequest.total_song_price > 0 && (
                                    <div className="flex items-center space-x-1 bg-yellow-400/10 px-2 py-1 rounded-full border border-yellow-400/20">
                                      <span className="text-xs text-yellow-400 font-semibold">{fmtMoney(groupedRequest.total_song_price)}</span>
                                    </div>
                                  )}
                                  {groupedRequest.total_tips > 0 && (
                                    <div className="flex items-center space-x-1 bg-red-400/10 px-2 py-1 rounded-full border border-red-400/20">
                                      <Heart className="w-3 h-3 text-red-400" />
                                      <span className="text-xs text-red-400 font-medium">{fmtMoney(groupedRequest.total_tips)}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Original Artist Row */}
                              <div className="text-sm text-slate-400 mb-1">
                                by {capitalizeWords(groupedRequest.original_artist || 'Unknown Artist')}
                              </div>
                              
                              {/* Requester and Time Row */}
                              <div className="flex items-center space-x-2 text-sm text-slate-400 mb-1">
                                {groupedRequest.request_count === 1 ? (
                                  <span>Req by {groupedRequest.requester_names[0]}</span>
                                ) : (
                                  <div className="flex items-center space-x-1">
                                    <span className={isTrialMode && highlightRequestCount === groupedRequest.song_id ? 'font-bold text-yellow-400 drop-shadow-[0_0_8px_rgba(234,179,8,0.8)] animate-pulse' : ''}>
                                      {groupedRequest.request_count}
                                    </span>
                                    <Users className={`w-3 h-3 ${isTrialMode && highlightRequestCount === groupedRequest.song_id ? 'text-yellow-400 drop-shadow-[0_0_8px_rgba(234,179,8,0.8)] animate-pulse' : ''}`} />
                                    <span>requests</span>
                                  </div>
                                )}
                                <span>•</span>
                                <span>{formatTimeAgo(groupedRequest.requests[0].created_at, currentTime)}</span>
                              </div>
                            </div>
                            
                            {/* Mark As Played Button - Compact */}
                            <div className="flex-shrink-0 flex items-stretch">
                              <motion.button
                                className={`flex flex-col items-center justify-center px-2 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl text-xs font-medium transition-colors min-h-[60px] w-16 shadow-[0_12px_30px_rgba(16,185,129,0.25)] ${
                                  isTrialMode && highlightMarkAsPlayedSongId === groupedRequest.song_id 
                                    ? 'animate-pulse drop-shadow-[0_0_12px_rgba(34,197,94,0.9)] shadow-[0_0_20px_rgba(34,197,94,0.6)] ring-2 ring-green-400 ring-offset-2 ring-offset-gray-900' 
                                    : ''
                                }`}
                                onClick={() => onMarkAsPlayed(groupedRequest)}
                                whileTap={{ scale: 0.95 }}
                              >
                                <CheckCircle className="w-3 h-3 mb-1" />
                                <span className="text-center leading-tight">
                                  <div>Mark</div>
                                  <div>As</div>
                                  <div>Played</div>
                                </span>
                              </motion.button>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {activeTab === 'played' && (
              <motion.div
                key="played"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
                className="w-full"
              >
                <div className="px-3 py-4">
                  <div className={`${ysr2Mobile.liveCard} flex items-center justify-between mb-4 p-4`}>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-5 h-5 text-emerald-300" />
                      <h3 className="text-lg font-black text-white">Played Songs</h3>
                    </div>
                    <span className="text-sm text-emerald-200">{playedRequests.length} played</span>
                  </div>
                  
                  {/* Persistent Manual Song Marking Button - Always at Top */}
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mb-4"
                  >
                    <motion.button
                      className={`w-full px-3 py-3 ${ysr2Mobile.secondaryButton} text-sm`}
                      onClick={() => setShowSongSearchModal(true)}
                      whileTap={{ scale: 0.95 }}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <Plus className="w-4 h-4" />
                        <span>Mark Song as Played</span>
                      </div>
                    </motion.button>
                  </motion.div>

                  {playedRequests.length === 0 ? (
                    <div className={`${ysr2Mobile.surface} text-center py-10 px-5`}>
                      <CheckCircle className="w-12 h-12 text-emerald-200 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-white mb-2">No Songs Played Yet</h3>
                      <p className="text-green-200 text-sm">
                        Songs you mark as played will appear here
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {playedRequests.slice(0, 10).map((request, index) => (
                        <motion.div
                          key={request.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          className={`${ysr2Mobile.liveCard} relative p-4 transition-colors`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              {/* Song Title Row */}
                              <div className="flex items-center space-x-2 mb-1">
                                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                                <h4 className="text-white font-semibold text-lg leading-tight break-words">{capitalizeWords(request.song_title)}</h4>
                              </div>
                              
                              {/* Original Artist Row */}
                              <div className="text-sm text-gray-400 mb-1">
                                by {capitalizeWords(request.original_artist || 'Unknown Artist')}
                              </div>
                              
                              {/* Requester and Time Row */}
                              <div className="text-sm text-gray-400 mb-1">
                                Req by {request.requester_name} • {formatTimeAgo(request.created_at, currentTime)}
                              </div>
                            </div>
                            
                            {/* Time */}
                            <div className="text-xs text-gray-500 flex-shrink-0 ml-4">
                              {formatTime(request.played_at)}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {activeTab === 'setlists' && (
              <motion.div
                key="setlists"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
                className="w-full"
              >
                <div className="px-3 py-4">
                  <div className={`${ysr2Mobile.surface} flex items-center justify-between mb-4 p-4`}>
                    <div className="flex items-center space-x-2">
                      <ListMusic className="w-5 h-5 text-blue-300" />
                      <h3 className="text-lg font-black text-white">Setlists</h3>
                    </div>
                    <span className="text-sm text-blue-200">{setlists?.length || 0} setlists</span>
                  </div>
                  
                  <div className="space-y-3">
                    {/* Show All Songs Option */}
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.05 }}
                      className={`${ysr2Mobile.surface} p-4`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <Music className="w-4 h-4 text-blue-400" />
                            <h4 className="font-medium text-white">All Songs</h4>
                            <span className="text-xs bg-blue-900/50 text-blue-400 px-2 py-1 rounded-full">
                              Default
                            </span>
                          </div>
                          <div className="text-sm text-gray-400">Display all songs in your catalogue</div>
                        </div>
                        <motion.button
                          className={`px-4 py-2 border rounded-lg ${
                            !activePlaylistId 
                              ? 'bg-blue-900/50 border-blue-600' 
                              : 'bg-gray-800/50 border-gray-600'
                          }`}
                          onClick={() => onSelectSetlist({ id: 'all', name: 'All Songs' })}
                          whileTap={{ scale: 0.95 }}
                        >
                          <span className={`text-sm ${
                            !activePlaylistId ? 'text-blue-400' : 'text-gray-400'
                          }`}>
                            {!activePlaylistId ? 'Active' : 'Select'}
                          </span>
                        </motion.button>
                      </div>
                    </motion.div>

                    {/* Setlists */}
                    {setlists && setlists.length > 0 ? (
                      setlists.map((setlist, index) => (
                        <motion.div
                          key={setlist.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: (index + 1) * 0.1 }}
                          className={`${ysr2Mobile.surface} p-4`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <ListMusic className="w-4 h-4 text-green-400" />
                                <h4 className="font-medium text-white">{setlist.name}</h4>
                                <span className="text-xs bg-green-900/50 text-green-400 px-2 py-1 rounded-full">
                                  {setlist.songs?.length || 0} songs
                                </span>
                              </div>
                              {setlist.description && (
                                <div className="text-sm text-gray-400">{setlist.description}</div>
                              )}
                            </div>
                            <motion.button
                              className={`px-4 py-2 border rounded-lg ${
                                activePlaylistId === setlist.id 
                                  ? 'bg-purple-900/50 border-purple-600' 
                                  : 'bg-gray-800/50 border-gray-600'
                              }`}
                              onClick={() => onSelectSetlist(setlist)}
                              whileTap={{ scale: 0.95 }}
                            >
                              <span className={`text-sm ${
                                activePlaylistId === setlist.id ? 'text-purple-400' : 'text-gray-400'
                              }`}>
                                {activePlaylistId === setlist.id ? 'Active' : 'Select'}
                              </span>
                            </motion.button>
                          </div>
                        </motion.div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <ListMusic className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-white mb-2">No Setlists Created</h3>
                        <p className="text-blue-200 text-sm mb-4">
                          Create setlists to organize your songs for different performances
                        </p>
                        <motion.button
                          className={`w-full px-4 py-3 ${ysr2Mobile.secondaryButton} text-sm`}
                          onClick={onNavigateToCatalogue}
                          whileTap={{ scale: 0.95 }}
                        >
                          <div className="flex items-center justify-center space-x-2">
                            <Plus className="w-4 h-4" />
                            <span>Go to Catalogue Management</span>
                          </div>
                        </motion.button>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'stats' && (
              <motion.div
                key="stats"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
                className="px-2"
              >
                <div className="p-4">
                  <div className={`${ysr2Mobile.surface} flex items-center space-x-2 mb-4 p-4`}>
                    <Users className="w-5 h-5 text-cyan-300" />
                    <h3 className="text-lg font-black text-white">Session Statistics</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className={`${ysr2Mobile.glowCard} text-center p-4`}>
                      <Volume2 className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                      <div className="text-xl font-bold text-white">{requests?.length || 0}</div>
                      <div className="text-xs text-gray-400">Total Requests</div>
                    </div>

                    <div className={`${ysr2Mobile.liveCard} text-center p-4`}>
                      <Mic className="w-6 h-6 text-green-400 mx-auto mb-2" />
                      <div className="text-xl font-bold text-white">{playedRequests.length}</div>
                      <div className="text-xs text-gray-400">Songs Played</div>
                    </div>

                    <div className="text-center p-4 bg-red-900/20 border border-red-500/40 rounded-xl">
                      <Heart className="w-6 h-6 text-red-400 mx-auto mb-2" />
                      <div className="text-xl font-bold text-white">{fmtMoney(sessionData?.totalTips || 0)}</div>
                      <div className="text-xs text-gray-400">Total Tips</div>
                    </div>

                    <div className="text-center p-4 bg-yellow-900/20 border border-yellow-500/40 rounded-xl">
                      <span className="text-2xl font-bold text-yellow-400 mx-auto mb-2 block">{getCurrencySymbol(artistCcy)}</span>
                      <div className="text-xl font-bold text-white">{fmtMoney(sessionData?.totalEarnings || 0)}</div>
                      <div className="text-xs text-gray-400">
                        {profile?.agent_id && profile?.agent_payout_preference === '80_20' ? 'Total Gross' : 'Total Earned'}
                      </div>
                    </div>
                  </div>

                  {/* GO LIVE Button - Only show when offline */}
                  {!isLive && (
                    <div className="mt-6">
                      <motion.button
                        onClick={() => {
                          // CRITICAL: Check if user has songs in the database - NO EXCEPTIONS!
                          console.log('🔍 DEBUG: Checking songs array:', songs);
                          console.log('🔍 DEBUG: Songs array length:', songs?.length);
                          
                          if (!songs || songs.length === 0) {
                            console.log('🚫 CRITICAL: No songs found in database, blocking goLive');
                            setShowAddSongsModal(true);
                            return;
                          }
                          
                          onGoLive && onGoLive(60);
                        }}
                        className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 rounded-lg text-white font-semibold transition-colors"
                        whileTap={{ scale: 0.95 }}
                      >
                        <div className="flex items-center justify-center space-x-2">
                          <Radio className="w-5 h-5" />
                          <span>Go Live</span>
                        </div>
                      </motion.button>
                    </div>
                  )}


                  {/* Recent Tips */}
                  {tips && tips.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Heart className="w-5 h-5 text-red-400" />
                        <h4 className="font-semibold text-red-100">Recent Tips</h4>
                      </div>
                      
                      {tips.slice(0, 5).map((tip, index) => (
                        <motion.div
                          key={tip.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                          className="flex items-center justify-between p-3 bg-red-900/20 border border-red-500/40 rounded-xl"
                        >
                          <div className="flex items-center space-x-3">
                            <Heart className="w-4 h-4 text-red-400" />
                            <div>
                              <div className="font-medium text-white">{fmtMoney(tip.amount)}</div>
                              <div className="text-xs text-gray-400">{tip.from_name}</div>
                            </div>
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatTime(tip.timestamp)}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          </div>
        )}
      </div>

      {/* Song Search Modal */}
      {showSongSearchModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-end justify-center z-[99999]"
          onClick={() => setShowSongSearchModal(false)}
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 20px)'
          }}
        >
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="w-full bg-gray-900 rounded-t-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '100vw',
              margin: '0',
              padding: '1rem',
              borderTopLeftRadius: '1rem',
              borderTopRightRadius: '1rem',
              position: 'relative',
              maxHeight: keyboardHeight > 0 
                ? `calc(100vh - ${keyboardHeight + 60}px)` 
                : 'calc(100vh - 60px)',
              minHeight: '400px',
              boxSizing: 'border-box'
            }}
          >
            {/* Drag Handle */}
            <div className="flex justify-center mb-4">
              <div className="w-12 h-1 bg-gray-600 rounded-full"></div>
            </div>
            {/* Modal Header */}
            <div className={`flex items-center justify-between ${keyboardHeight > 0 ? 'mb-2' : 'mb-4'}`}>
              <div>
                <h3 className={`font-semibold text-white ${keyboardHeight > 0 ? 'text-base' : 'text-lg'}`}>Mark Song as Played</h3>
                <p className="text-gray-400 text-sm">
                  {songSearchQuery ? `Search results (${filteredSongs.length})` : `All songs (${songs.length})`}
                </p>
              </div>
              <motion.button
                onClick={() => setShowSongSearchModal(false)}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                whileTap={{ scale: 0.9 }}
              >
                <X className="w-5 h-5 text-gray-400" />
              </motion.button>
            </div>

            {/* Search Input */}
            <div className={`relative ${keyboardHeight > 0 ? 'mb-2' : 'mb-4'}`}>
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search songs or scroll through all..."
                value={songSearchQuery}
                onChange={(e) => handleSongSearch(e.target.value)}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                style={{
                  fontSize: '16px', // Prevents zoom on iOS
                  WebkitAppearance: 'none',
                  borderRadius: '0.5rem'
                }}
              />
            </div>

            {/* Song List */}
            <div 
              className="overflow-y-auto space-y-2"
              style={{ 
                maxHeight: keyboardHeight > 0 
                  ? `calc(100vh - ${keyboardHeight + 200}px)` 
                  : 'calc(100vh - 200px)',
                minHeight: '200px',
                paddingBottom: '20px',
                boxSizing: 'border-box'
              }}
            >
              {songSearchQuery ? (
                // Show filtered results when searching
                filteredSongs.length > 0 ? (
                  filteredSongs.map((song) => (
                    <motion.div
                      key={song.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-center justify-between p-3 bg-gray-800 rounded-lg hover:bg-gray-700 cursor-pointer transition-colors"
                      onClick={() => markSongAsPlayedFromSearch(song)}
                    >
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-medium truncate">{capitalizeWords(song.title)}</h4>
                        {song.artist && (
                          <p className="text-gray-400 text-sm truncate">by {capitalizeWords(song.artist)}</p>
                        )}
                      </div>
                      <motion.div
                        className="flex-shrink-0 ml-3 p-2 bg-green-600 rounded-lg"
                        whileTap={{ scale: 0.9 }}
                      >
                        <CheckCircle className="w-4 h-4 text-white" />
                      </motion.div>
                    </motion.div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Music className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-400">No songs found</p>
                    <p className="text-gray-500 text-sm">Try a different search term</p>
                  </div>
                )
              ) : (
                // Show all songs by default
                songs.length > 0 ? (
                  songs.map((song) => (
                    <motion.div
                      key={song.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-center justify-between p-3 bg-gray-800 rounded-lg hover:bg-gray-700 cursor-pointer transition-colors"
                      onClick={() => markSongAsPlayedFromSearch(song)}
                    >
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-medium truncate">{capitalizeWords(song.title)}</h4>
                        {song.artist && (
                          <p className="text-gray-400 text-sm truncate">by {capitalizeWords(song.artist)}</p>
                        )}
                      </div>
                      <motion.div
                        className="flex-shrink-0 ml-3 p-2 bg-green-600 rounded-lg"
                        whileTap={{ scale: 0.9 }}
                      >
                        <CheckCircle className="w-4 h-4 text-white" />
                      </motion.div>
                    </motion.div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Music className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-400">No songs in your catalogue</p>
                    <p className="text-gray-500 text-sm">Add songs to your catalogue first</p>
                  </div>
                )
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
      
      {/* Add Songs Required Modal */}
      <AddSongsRequiredModal
        isOpen={showAddSongsModal}
        onClose={() => setShowAddSongsModal(false)}
        onNavigateToCatalogue={() => {
          setShowAddSongsModal(false);
          if (isAddSongWizardCompleted(profile, songs?.length ?? 0)) {
            navigate('/admin/catalogue');
          } else {
            setShowSongWizard(true);
          }
        }}
      />

      {/* Mobile Session Summary Modal */}
      {showSessionComplete && sessionCompleteData && (() => {
        console.log('🔍 DEBUG: Mobile session summary modal showing with data:', sessionCompleteData);
        return (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="fixed inset-0 z-[99999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        >
          {/* Particle Effects */}
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ 
                opacity: 0, 
                scale: 0,
                x: Math.random() * 400 - 200,
                y: Math.random() * 400 - 200
              }}
              animate={{ 
                opacity: [0, 1, 0],
                scale: [0, 1, 0],
                x: Math.random() * 400 - 200,
                y: Math.random() * 400 - 200
              }}
              transition={{ 
                duration: 2,
                delay: i * 0.2,
                repeat: Infinity,
                repeatDelay: 3
              }}
              className="absolute w-2 h-2 bg-yellow-400 rounded-full"
            />
          ))}
          <motion.div 
            initial={{ opacity: 0, scale: 0.5, y: 100, rotateX: -15 }}
            animate={{ opacity: 1, scale: 1, y: 0, rotateX: 0 }}
            exit={{ opacity: 0, scale: 0.5, y: 100, rotateX: 15 }}
            transition={{ 
              type: "spring", 
              stiffness: 260, 
              damping: 20,
              duration: 0.8
            }}
            className="bg-gray-900 border border-gray-700 rounded-xl max-w-md w-full max-h-[90vh] flex flex-col shadow-2xl shadow-yellow-500/20"
          >
            {/* Header */}
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.4 }}
              className="flex-shrink-0 p-4 border-b border-gray-700"
            >
              <div className="text-center">
                <motion.div 
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                  className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3"
                >
                  <motion.span 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.5, type: "spring", stiffness: 300 }}
                    className="text-2xl"
                  >
                    🎉
                  </motion.span>
                </motion.div>
                <motion.h3 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.3 }}
                  className="text-lg font-bold text-green-400"
                >
                  Session Complete!
                </motion.h3>
                <motion.p 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.3 }}
                  className="text-gray-400 text-sm"
                >
                  Your live session has ended
                </motion.p>
              </div>
            </motion.div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Session Duration */}
              <div className="text-center">
                <p className="text-gray-400 text-xs">Session Duration</p>
                <p className="text-lg font-semibold text-white">
                  {sessionCompleteData.startTime && sessionCompleteData.endTime ? 
                    `${Math.round((sessionCompleteData.endTime - sessionCompleteData.startTime) / 60000)} minutes` : 
                    'N/A'
                  }
                </p>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-2 gap-3">
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="text-center p-3 bg-green-900/20 border border-green-700/40 rounded-lg"
                >
                  <motion.div 
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.4, type: "spring", stiffness: 300, damping: 15 }}
                    className="text-lg font-bold text-green-400"
                  >
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.6, duration: 0.3 }}
                    >
                      {sessionCompleteData.totalRequests || 0}
                    </motion.span>
                  </motion.div>
                  <div className="text-xs text-gray-400">Songs</div>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                  className="text-center p-3 bg-blue-900/20 border border-blue-700/40 rounded-lg"
                >
                  <motion.div 
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.5, type: "spring", stiffness: 300, damping: 15 }}
                    className="text-lg font-bold text-blue-400"
                  >
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.7, duration: 0.3 }}
                    >
                      {fmtMoney(sessionCompleteData.totalEarnings || 0)}
                    </motion.span>
                  </motion.div>
                  <div className="text-xs text-gray-400">Earnings</div>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                  className="text-center p-3 bg-purple-900/20 border border-purple-700/40 rounded-lg"
                >
                  <motion.div 
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.6, type: "spring", stiffness: 300, damping: 15 }}
                    className="text-lg font-bold text-purple-400"
                  >
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.8, duration: 0.3 }}
                    >
                      {sessionCompleteData.totalTips || 0}
                    </motion.span>
                  </motion.div>
                  <div className="text-xs text-gray-400">Tips</div>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
                  className="text-center p-3 bg-yellow-900/20 border border-yellow-700/40 rounded-lg"
                >
                  <motion.div 
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.7, type: "spring", stiffness: 300, damping: 15 }}
                    className="text-lg font-bold text-yellow-400"
                  >
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.9, duration: 0.3 }}
                    >
                      {fmtMoney((sessionCompleteData.totalEarnings || 0) + (sessionCompleteData.totalTips || 0))}
                    </motion.span>
                  </motion.div>
                  <div className="text-xs text-gray-400">Total</div>
                </motion.div>
              </div>

              {/* Performance Summary */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.4 }}
                className="text-center p-3 bg-gray-800/50 rounded-lg"
              >
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.9, duration: 0.3 }}
                  className="text-gray-400 text-xs mb-1"
                >
                  Performance Summary
                </motion.p>
                <motion.p 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.0, duration: 0.3 }}
                  className="text-white text-sm"
                >
                  {sessionCompleteData.totalRequests > 0 ? 
                    `Great performance! You played ${sessionCompleteData.totalRequests} song${sessionCompleteData.totalRequests > 1 ? 's' : ''} and earned ${fmtMoney((sessionCompleteData.totalEarnings || 0) + (sessionCompleteData.totalTips || 0))}.` :
                    'No songs were played this session.'
                  }
                </motion.p>
              </motion.div>
            </div>

            {/* Footer */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.4 }}
              className="flex-shrink-0 p-4 border-t border-gray-700"
            >
              <motion.button
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                transition={{ delay: 1.2, type: "spring", stiffness: 200 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onCloseSessionComplete}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-medium transition-colors"
              >
                Continue
              </motion.button>
            </motion.div>
          </motion.div>
        </motion.div>
        );
      })()}

      {/* Trial Demo Modal */}
      {showTrialDemoModal && setShowTrialDemoModal && (
        <Dialog open={showTrialDemoModal} onOpenChange={setShowTrialDemoModal}>
          <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-center">
                {hasGoneLiveBefore && daysSinceLastLive !== null && daysSinceLastLive > 7
                  ? "🔄 Need a Refresher?"
                  : "🎵 First Time Going Live?"}
              </DialogTitle>
              <DialogDescription className="text-center text-gray-300 mt-2">
                {hasGoneLiveBefore && daysSinceLastLive !== null && daysSinceLastLive > 7
                  ? "Take a quick trial to refresh your memory!"
                  : "Try our interactive demo to see how song requests work!"}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-4">
                <h3 className="text-white font-semibold mb-2">What you'll learn:</h3>
                <ul className="text-gray-300 text-sm space-y-1 list-disc list-inside">
                  <li>How requests appear in your dashboard</li>
                  <li>How to mark songs as played</li>
                  <li>How tips work</li>
                  <li>How multiple requests for the same song work</li>
                </ul>
              </div>
            </div>
            <DialogFooter className="flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowTrialDemoModal(false);
                  if (user?.email !== 'trial@trial.com') {
                    localStorage.setItem('trial_demo_dismissed', 'true');
                  }
                }}
                className="w-full sm:w-auto"
              >
                Maybe Later
              </Button>
              <Button 
                onClick={() => {
                  if (onStartTrialDemo) {
                    onStartTrialDemo();
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
                disabled={songs.length < 3}
              >
                Start Demo!
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => {
                  dismissTrialDemoPermanently();
                }}
                className="w-full sm:w-auto text-gray-400 hover:text-gray-300 text-sm"
              >
                Don't Show Me This Again
              </Button>
            </DialogFooter>
            {songs.length < 3 && (
              <p className="text-yellow-400 text-xs text-center mt-2">
                You need at least 3 songs in your catalogue to run the demo.
              </p>
            )}
          </DialogContent>
        </Dialog>
      )}

    </div>
  );
};

export default MobileLiveDashboard;
